// src/pages/vendor/vendorProfile/LegalDisclaimer.jsx

import { ArrowLeft } from 'lucide-react';
import BottomNav from '../../../components/layouts/Footer/BottomNav';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

export default function LegalDisclaimer() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <main className="container mx-auto px-4 md:pb-6 pt-4 pb-20 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl flex items-center gap-3 justify-center sm:text-4xl font-extrabold text-center mb-8 tracking-tight text-gray-900 dark:text-gray-100">
            <ArrowLeft onClick={() => navigate(-1)} className="cursor-pointer size-8" />
            Legal Disclaimer
          </h1>

          <div className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-base">
            <p className="text-sm text-gray-500 dark:text-gray-400">Last Updated: November 2024</p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">General Information</h3>
            <p>
              The information provided on BigLocal.in (&quot;Platform&quot;) is for general
              informational purposes only. While we strive to keep the information accurate and
              up-to-date, we make no representations or warranties of any kind, express or implied,
              about the completeness, accuracy, reliability, suitability, or availability of the
              Platform or the information, products, services, or related graphics contained on the
              Platform.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Use at Your Own Risk</h3>
            <p>
              Any reliance you place on such information is strictly at your own risk. In no event
              will BigLocal.in be liable for any loss or damage including without limitation,
              indirect or consequential loss or damage, or any loss or damage whatsoever arising
              from loss of data or profits arising out of, or in connection with, the use of this
              Platform.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Third-Party Content</h3>
            <p>
              Through this Platform, you may be able to link to other websites or content provided
              by third parties. BigLocal.in has no control over the nature, content, and
              availability of those sites and third-party content. The inclusion of any links or
              third-party content does not necessarily imply a recommendation or endorse the views
              expressed within them.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Product Information</h3>
            <p>
              We make every effort to display product information, including descriptions, images,
              and pricing, as accurately as possible. However, we do not warrant that product
              descriptions, images, pricing, or other content on the Platform is accurate, complete,
              reliable, current, or error-free.
            </p>
            <p>
              Product images are for illustrative purposes only. Actual products may vary in
              appearance, packaging, and specifications. Colors and sizes may vary slightly from
              what appears on your screen due to monitor settings.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Pricing and Availability</h3>
            <p>
              All prices are subject to change without notice. We reserve the right to modify or
              discontinue products at any time without liability. Product availability is not
              guaranteed and may vary by location and vendor.
            </p>
            <p>
              In the event of a pricing error, we reserve the right to cancel orders placed at the
              incorrect price and issue a refund or offer the product at the correct price.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Vendor Responsibility</h3>
            <p>
              BigLocal.in acts as a platform connecting customers with local vendors. The vendors
              listed on our Platform are independent third parties. BigLocal.in is not responsible
              for the quality, safety, legality, or any other aspect of the products or services
              provided by vendors.
            </p>
            <p>
              Any disputes regarding products, services, or transactions should be resolved directly
              with the respective vendor. However, we will assist in facilitating communication and
              resolution where possible.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Health and Safety</h3>
            <p>
              For food products, we recommend that you check product labels, expiry dates, and
              allergen information before consumption. If you have specific dietary requirements or
              allergies, please verify product information with the vendor before ordering.
            </p>
            <p>
              BigLocal.in is not liable for any adverse reactions, allergies, or health issues
              arising from the consumption of products purchased through the Platform.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Delivery and Logistics</h3>
            <p>
              Delivery times are estimates and may vary due to factors beyond our control, including
              weather conditions, traffic, vendor availability, and other unforeseen circumstances.
              BigLocal.in is not liable for delays in delivery.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">User-Generated Content</h3>
            <p>
              Reviews, ratings, and other user-generated content on the Platform represent the
              opinions of individual users and do not reflect the views of BigLocal.in. We do not
              verify the accuracy of user-generated content and are not responsible for any
              decisions made based on such content.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Technical Issues</h3>
            <p>
              We strive to ensure that the Platform is available 24/7. However, we do not guarantee
              uninterrupted access and are not liable for any technical issues, downtime, or data
              loss that may occur.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Payment Processing</h3>
            <p>
              Payment processing is handled by third-party payment gateways. BigLocal.in is not
              responsible for any issues arising from payment processing, including but not limited
              to transaction failures, unauthorized charges, or data breaches at the payment gateway
              level.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Intellectual Property</h3>
            <p>
              All content on the Platform, including text, graphics, logos, images, and software, is
              the property of BigLocal.in or its content suppliers and is protected by intellectual
              property laws. Unauthorized use of any content may violate copyright, trademark, and
              other laws.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Limitation of Liability</h3>
            <p>
              To the fullest extent permitted by law, BigLocal.in, its directors, employees,
              partners, agents, suppliers, or affiliates shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages, including without limitation,
              loss of profits, data, use, goodwill, or other intangible losses, resulting from:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Your access to or use of or inability to access or use the Platform</li>
              <li>Any conduct or content of any third party on the Platform</li>
              <li>Any content obtained from the Platform</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content</li>
            </ul>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Indemnification</h3>
            <p>
              You agree to defend, indemnify, and hold harmless BigLocal.in and its affiliates from
              and against any claims, liabilities, damages, losses, and expenses, including without
              limitation, reasonable legal and accounting fees, arising out of or in any way
              connected with your access to or use of the Platform or your violation of these terms.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Governing Law</h3>
            <p>
              This disclaimer and any disputes arising out of or related to it shall be governed by
              and construed in accordance with the laws of India, without regard to its conflict of
              law provisions. Courts in New Delhi shall have exclusive jurisdiction.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Changes to Disclaimer</h3>
            <p>
              We reserve the right to modify this disclaimer at any time. Changes will be effective
              immediately upon posting on the Platform. Your continued use of the Platform after any
              changes constitutes acceptance of the modified disclaimer.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Contact Information</h3>
            <p>
              If you have any questions about this Legal Disclaimer, please contact us at
              yogesh@revvtch.com
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Severability</h3>
            <p>
              If any provision of this disclaimer is found to be unenforceable or invalid, that
              provision shall be limited or eliminated to the minimum extent necessary so that this
              disclaimer shall otherwise remain in full force and effect.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">Force Majeure</h3>
            <p>
              BigLocal.in shall not be liable for any failure to perform its obligations where such
              failure results from any cause beyond BigLocal.in&apos;s reasonable control,
              including, without limitation, mechanical, electronic, or communications failure or
              degradation, natural disasters, pandemics, government actions, or acts of terrorism.
            </p>

            <h3 className="font-bold text-gray-700 dark:text-gray-300">No Professional Advice</h3>
            <p>
              The information provided on the Platform is not intended to be a substitute for
              professional advice. Always seek the advice of qualified professionals with any
              questions you may have regarding health, nutrition, legal, or financial matters.
            </p>
          </div>
        </div>
      </main>
      <BottomNav />
    </>
  );
}
